import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { path } from 'ramda';

import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { withViewport } from '../../util/contextHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  Button,
  IconAdd,
  IconClose,
  IconSearch,
  LocalizationMenu,
  Logo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  NotificationBadge,
  TopbarMenu,
  SearchBarContext,
  SmartAppBanner,
} from '../../components';
import { TopbarSearchForm } from '../../forms';

import MenuIcon from './MenuIcon';
import css from './Topbar.css';

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const { bool } = PropTypes;

GenericError.propTypes = {
  show: bool.isRequired,
};

const Topbar = props => {
  const {
    className,
    rootClassName,
    isAuthenticated,
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount,
    messagesNotificationCount,
    bookingsNotificationCount,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    currentSearchParams,
    onLogout,
    viewport,
    history,
    intl,
    onUpdateProfile,
  } = props;
  const [scrolled, setScrolled] = useState();
  const [offSet, setOffSet] = useState(0);
  const [scrollToTop, setScrollToTop] = useState(false);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const searchBarContext = useContext(SearchBarContext);
  const showSearch = searchBarContext.showSearch;
  const setShowSearch = searchBarContext.setShowSearch;
  const prevOffSet = useRef(offSet);
  const { menu, date, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const isMenuOpen = menu === 'open';
  useEffect(() => {
    if (!isMenuOpen || notificationCount <= 0) return;

    onUpdateProfile({privateData: {badge: 0}});
  }, [isMenuOpen]);
  const isBrowser = typeof window !== 'undefined';
  const isSearchPage = currentPage === 'SearchPage';

  if (isSearchPage && offSet === 0 && prevOffSet.current === 0) {
    setOffSet(1);
    setScrolled(true);
  };


  useEffect(() => {
    if (['AuthenticationPage'].includes(currentPage)) return;
    const handleScroll = () => {
      if (window.pageYOffset > 1) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      if (window.pageYOffset > offSet) {
        setScrollToTop(false);
        setScrollToBottom(true);
      } else if (window.pageYOffset < offSet) {
        setScrollToBottom(false);
        setScrollToTop(true);
      }

      prevOffSet.current = offSet;
      setOffSet(window.pageYOffset);

      const offSetDiff = Math.abs(prevOffSet.current - window.pageYOffset);

      if (offSetDiff > 20 && viewport.width >= 1024) {
        setShowSearch(false);
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isBrowser && window.pageYOffset])

  const handleMenuOpen = () => {
    redirectToURLWithModalState(props, 'menu');
  };
  const handleMenuClose = () => {
    redirectToURLWithoutModalState(props, 'menu');
  };
  const handleShowSearch = () => {
    setShowSearch(true);
  };
  const handleHideSearch = () => {
    if (!showSearch) return;

    setShowSearch(false);
  };
  const handleSubmit = (values) => {
    const { location, date } = values;
    const { search, selectedPlace } = location;
    const { origin, bounds, city } = selectedPlace;
    const originMaybe = config.sortSearchByDistance ? { origin } : {};
    const dateRaw = path(['date'], date);
    const dateMaybe = dateRaw && dateRaw.toISOString();
    const searchParams = {
      ...currentSearchParams,
      ...originMaybe,
      address: search,
      bounds,
      city,
      date: dateMaybe,
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };
  const handleLogout = () => {
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration());

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (config.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };
  const navigationMenu = (
    <TopbarMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      onLogout={handleLogout}
      messagesNotificationCount={messagesNotificationCount}
      bookingsNotificationCount={bookingsNotificationCount}
      intl={intl}
    />
  );

  // Only render current search if full place object is available in the URL params
  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    date,
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
      : null,
  };

  const menuText = intl.formatMessage({ id: 'Topbar.menu' });
  const addListingLinkText = intl.formatMessage({ id: currentPage === 'SimulatorPage' ? 'Topbar.addListingLink' : 'Topbar.simulatorLink' })
  const searchButtonText = intl.formatMessage({ id: 'Topbar.search' })

  const classes = classNames(rootClassName || css.root, className);

  const fixed = scrolled || !['LandingPage','SimulatorPage', 'AuthenticationPage', 'WhoWeArePage'].includes(currentPage) || showSearch;
  const lightColor = ['LandingPage', 'SimulatorPage', 'WhoWeArePage'].includes(currentPage) && !scrolled && !showSearch;
  const disableLightColor = ['SimulatorPage'].includes(currentPage) && !scrolled;
  const showMobileSearchBar = scrolled && ['LandingPage', 'ListingPage', 'SearchPage', 'TransactionPage', 'BlogPage', 'NotFoundPage', 'HowItWorksPage', 'WhoWeArePage'].includes(currentPage);
  const showAddListingLink = scrolled && ['LandingPage','SimulatorPage', 'WhoWeArePage'].includes(currentPage) || ['BlogPage', 'NotFoundPage', 'HowItWorksPage'].includes(currentPage);
  const showSearchButton = scrolled && ['LandingPage', 'WhoWeArePage'].includes(currentPage) || ['ListingPage', 'SearchPage', 'TransactionPage', 'BlogPage', 'NotFoundPage', 'HowItWorksPage'].includes(currentPage);
  const showProviderButton = scrolled && ['LandingPage', 'SimulatorPage', 'BlogPage', 'NotFoundPage', 'HowItWorksPage', 'WhoWeArePage'].includes(currentPage);

  const hiddenNavContainer = scrollToBottom && offSet > 0 && ['LandingPage', 'ListingPage', 'SearchPage'].includes(currentPage);

  const navContainerClasses = classNames(
    css.navContainer,
    {
      [css.fixedNavContainer]: fixed,
      [css.hiddenNavContainer]: hiddenNavContainer,
    });

  const menuClasses = classNames(css.menuContainer, { [css.fixedMenuContainer]: fixed });
  const menuTextClasses = classNames({ [css.lightText]: lightColor && !disableLightColor }, css.menuText);
  const badgeOnMenuClasses = classNames(css.badgeOnMenu, { [css.hiddenBadge]: hiddenNavContainer });
  const localizationMenuClasses = classNames(css.localizationMenu, { [css.fixedLocalizationMenu]: fixed });
  const homeClasses = classNames(css.home, { [css.fixedHome]: fixed });
  const logoClasses = classNames(css.logo, { [css.lightLogo]: lightColor, [css.fixedLogo]: fixed });
  const providerCTAClasses = classNames(css.providerCTA, { [css.hiddenProviderCTA]: !showProviderButton})

  const searchBarMobileClasses = classNames(
    css.mobileSearchBar,
    {
      [css.hiddenMobileSearchBar]: !showMobileSearchBar || (showMobileSearchBar && showSearch),
      [css.searchPageMobile]: isSearchPage,
    });
  const searchClasses = classNames(css.searchTopContainer, { [css.hiddenSearchTopContainer]: !showSearch });
  const backgroundClasses = classNames(css.hiddenBackground, { [css.opacityBackground]: showSearch });
  const notificationDot = notificationCount > 0 && <NotificationBadge className={badgeOnMenuClasses} count={notificationCount} />;

  return (
    <div className={classes}>
      <SmartAppBanner />
      <div className={backgroundClasses}onClick={handleHideSearch} />
      <div className={navContainerClasses}>
        <Button
          rootClassName={menuClasses}
          onClick={handleMenuOpen}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon />
          {notificationDot}
          <span className={menuTextClasses}>{menuText}</span>
        </Button>
        <LocalizationMenu className={localizationMenuClasses} />
        {showSearchButton && !showSearch ? (
          <Button
            rootClassName={css.searchButtonDesktop}
            onClick={handleShowSearch}
          >
            <span className={css.searchButtonText}>{searchButtonText}</span>
            <IconSearch />
          </Button>
        ) : null}
        {showAddListingLink && !showSearch ? (
          <NamedLink
            className={css.addListingLink}
            name={currentPage === 'SimulatorPage' ? 'NewListingPage' : 'SimulatorPage'}
          >
            <span className={css.addListingLinkText}>{addListingLinkText}</span>
            <IconAdd className={css.addListingIcon} />
          </NamedLink>
        ) : null}
        <NamedLink
          className={homeClasses}
          name="LandingPage"
          title={intl.formatMessage({ id: 'Topbar.logoIcon' })}
        >
          <Logo format="desktop" className={logoClasses} />
        </NamedLink>
      </div>

      <div className={searchBarMobileClasses}>
        <Button
          rootClassName={css.searchButton}
          onClick={handleShowSearch}
        >
          <span className={css.searchButtonText}>{searchButtonText}</span>
          <IconSearch />
        </Button>
      </div>

      <div className={searchClasses}>
        <Button
          rootClassName={css.closeSearch}
          onClick={handleHideSearch}
        >
          <FormattedMessage id='Topbar.closeSearch' />
          <IconClose className={css.iconClose} />
        </Button>
        <TopbarSearchForm
          className={css.searchForm}
          form="TopbarSearchForm"
          onSubmit={handleSubmit}
          hideSearch={handleHideSearch}
          initialValues={initialSearchFormValues}
        />
      </div>

      <NamedLink
        className={providerCTAClasses}
        name={currentPage === 'SimulatorPage' ? 'NewListingPage' : 'SimulatorPage'}
      >
        <span>{addListingLinkText}</span>
        <IconAdd className={css.addListingIcon} />
      </NamedLink>

      <Modal
        id="TopbarMenu"
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        onManageDisableScrolling={onManageDisableScrolling}
        containerClassName={css.menuModalContainer}
        scrollLayerClassName={css.scrollLayer}
        closeButtonClassName={css.closeMenuButton}
        isMenu
      >
        {authInProgress ? null : navigationMenu}
      </Modal>

      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
}

Topbar.defaultProps = {
  className: null,
  rootClassName: null,
  notificationCount: 0,
  messagesNotificationCount: 0,
  bookingsNotificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
};

const { func, number, shape, string } = PropTypes;

Topbar.propTypes = {
  className: string,
  rootClassName: string,
  isAuthenticated: bool.isRequired,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  messagesNotificationCount: number,
  bookingsNotificationCount: number,
  onLogout: func.isRequired,
  onUpdateProfile: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

Topbar.displayName = 'Topbar';

export default compose(withViewport, injectIntl)(Topbar);



// WEBPACK FOOTER //
// ./src/components/Topbar/Topbar.js